import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ArrowRight, ImagePlus, Stars01 } from "@untitledui/icons"
import type { ApiEstimate } from "@/lib/api/types"
import type { PublicShopProfile } from "@/lib/api/public-consumer"
import type { ConsumerPollStatus } from "./use-consumer-estimate-polling"
import { summarizeDamage } from "./summarize"
import { RevealStage } from "./reveal-stage"
import { CONSUMER_V2 } from "./copy"

// The reveal phase controller. The orchestrator owns polling (the analysis
// pill during extras reads the same stream); this component turns
// {estimate, status} into the right scene: a short analyzing beat (600ms
// minimum so an instant mock read never flashes — the old fixed 2.8s floor is
// gone), then the pine RevealStage, an honest "hard to read" state, or the
// fallback when the agent never came back / there were no photos.

const ANALYZING_MESSAGES = [
  "Looking at your photos…",
  "Detecting impact zones…",
  "Reading the damage…",
]
const MIN_BEAT_MS = 600

export function AhaReveal({
  shop,
  first,
  phone,
  estimate,
  status,
  noPhotos,
  heroPhotoUrl,
  heroSideLabel,
  onContinue,
  onAddPhoto,
  onRevealSeen,
}: {
  shop: PublicShopProfile
  first: string | null
  phone: string
  estimate: ApiEstimate | null
  status: ConsumerPollStatus
  // The consumer sent no photos at all — skip straight to the fallback.
  noPhotos: boolean
  heroPhotoUrl: string | null
  heroSideLabel: string
  onContinue: () => void
  onAddPhoto: () => void
  onRevealSeen: () => void
}) {
  const [minElapsed, setMinElapsed] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), MIN_BEAT_MS)
    return () => clearTimeout(t)
  }, [])

  const dataReady = status === "ready" && (estimate?.lineItems?.length ?? 0) > 0
  const revealed = !noPhotos && dataReady && minElapsed
  const failed = noPhotos || (status === "timed-out" && !dataReady)

  // First time the reveal actually shows → stamp revealSeenAt.
  const seenRef = useRef(false)
  useEffect(() => {
    if (revealed && !seenRef.current) {
      seenRef.current = true
      onRevealSeen()
    }
  }, [revealed, onRevealSeen])

  const readout = revealed && estimate ? summarizeDamage(estimate.lineItems) : null

  return (
    <AnimatePresence mode="wait">
      {!revealed && !failed && (
        <QuietScreen key="analyzing">
          <AnalyzingScene heroPhotoUrl={heroPhotoUrl} />
        </QuietScreen>
      )}

      {revealed && readout && readout.areas.length > 0 && (
        <RevealStage
          key="reveal"
          shop={shop}
          first={first}
          readout={readout}
          heroPhotoUrl={heroPhotoUrl}
          heroSideLabel={heroSideLabel}
          onContinue={onContinue}
          onAddPhoto={onAddPhoto}
        />
      )}

      {revealed && readout && readout.areas.length === 0 && (
        <QuietScreen key="uncertain">
          <UncertainScene
            shop={shop}
            heroPhotoUrl={heroPhotoUrl}
            onContinue={onContinue}
            onAddPhoto={onAddPhoto}
          />
        </QuietScreen>
      )}

      {failed && (
        <QuietScreen key="fallback">
          <FallbackScene
            shop={shop}
            phone={phone}
            onContinue={onContinue}
            onAddPhoto={onAddPhoto}
          />
        </QuietScreen>
      )}
    </AnimatePresence>
  )
}

// The non-pine states share the flow's quiet ground.
function QuietScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-zinc-50 to-white">
      <div className="mx-auto flex min-h-[100dvh] max-w-[560px] flex-col px-4 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-5 md:px-6">
        {children}
      </div>
    </div>
  )
}

function AnalyzingScene({ heroPhotoUrl }: { heroPhotoUrl: string | null }) {
  const [msgIndex, setMsgIndex] = useState(0)
  const [heroBroken, setHeroBroken] = useState(false)
  useEffect(() => {
    const t = setInterval(
      () => setMsgIndex((i) => (i + 1) % ANALYZING_MESSAGES.length),
      1100,
    )
    return () => clearInterval(t)
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="flex flex-1 flex-col justify-center py-6"
    >
      <div className="relative mx-auto aspect-[4/3] w-full max-w-[420px] overflow-hidden rounded-3xl border border-zinc-200 bg-zinc-900 shadow-xl">
        {heroPhotoUrl && !heroBroken ? (
          <img
            src={heroPhotoUrl}
            alt="Analyzing your vehicle"
            onError={() => setHeroBroken(true)}
            className="h-full w-full object-cover opacity-90"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-zinc-500">
            <Stars01 className="size-10" aria-hidden />
          </div>
        )}
        <motion.div
          className="pointer-events-none absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-emerald-400/40 to-transparent"
          initial={{ top: "-30%" }}
          animate={{ top: "110%" }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-emerald-400/30" />
      </div>

      <div className="mt-7 text-center" role="status">
        <div className="mx-auto flex size-12 items-center justify-center">
          <span className="size-6 animate-spin rounded-full border-2 border-zinc-200 border-t-emerald-500" />
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={msgIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="mt-2 text-[15px] font-semibold text-zinc-700"
          >
            {ANALYZING_MESSAGES[msgIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

// Agent found nothing mappable / no line items — never fake it. Honest, calm,
// and still moves forward to a human.
function UncertainScene({
  shop,
  heroPhotoUrl,
  onContinue,
  onAddPhoto,
}: {
  shop: PublicShopProfile
  heroPhotoUrl: string | null
  onContinue: () => void
  onAddPhoto: () => void
}) {
  const [heroBroken, setHeroBroken] = useState(false)
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-1 flex-col py-4"
    >
      {heroPhotoUrl && !heroBroken && (
        <div className="overflow-hidden rounded-3xl border border-zinc-200 shadow-md">
          <img
            src={heroPhotoUrl}
            alt="Your vehicle"
            onError={() => setHeroBroken(true)}
            className="max-h-[260px] w-full object-cover"
          />
        </div>
      )}
      <h2 className="mt-5 text-[24px] font-bold leading-tight tracking-tight text-zinc-900">
        Thanks, we've got your photos
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
        The damage is a little hard to read from here. A clearer close-up helps,
        or {shop.name} can take it from this point.
      </p>
      <div className="mt-6 space-y-3">
        <button
          type="button"
          onClick={onAddPhoto}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-zinc-900 text-[15px] font-semibold text-white shadow-lg shadow-zinc-900/20 transition-all hover:bg-black"
        >
          <ImagePlus className="size-4" aria-hidden />
          Add a clearer photo
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="h-12 w-full rounded-xl text-[14px] font-semibold text-zinc-500 transition-colors hover:text-zinc-900"
        >
          Send what I have to {shop.name.split(" ")[0]}
        </button>
      </div>
    </motion.div>
  )
}

function FallbackScene({
  shop,
  phone,
  onContinue,
  onAddPhoto,
}: {
  shop: PublicShopProfile
  phone: string
  onContinue: () => void
  onAddPhoto: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-1 flex-col justify-center py-10 text-center"
    >
      <div className="mx-auto grid size-16 place-items-center rounded-full bg-emerald-50 text-emerald-600">
        <Stars01 className="size-8" aria-hidden />
      </div>
      <h2 className="mt-5 text-[24px] font-bold tracking-tight text-zinc-900">
        {CONSUMER_V2.fallback.title}
      </h2>
      <p className="mx-auto mt-2 max-w-xs text-[14px] leading-relaxed text-zinc-500">
        {CONSUMER_V2.fallback.body(shop.name, phone || "you")}
      </p>
      <div className="mx-auto mt-7 w-full max-w-sm space-y-3">
        <button
          type="button"
          onClick={onContinue}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-zinc-900 text-[15px] font-semibold text-white shadow-lg shadow-zinc-900/20 transition-all hover:bg-black"
        >
          Continue
          <ArrowRight className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onAddPhoto}
          className="h-12 w-full rounded-xl text-[14px] font-semibold text-zinc-500 transition-colors hover:text-zinc-900"
        >
          Add another photo
        </button>
      </div>
    </motion.div>
  )
}
